import useSWR from "swr"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"

export const uploadFileWithProgress = (
    url: string,
    file: ArrayBuffer | File,
    onProgress: (progress: number) => void
) => {
    return new Promise<XMLHttpRequest>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open("PUT", url)
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                onProgress(event.loaded / event.total)
            }
        }
        xhr.onload = () => {
            if (xhr.status === 200) resolve(xhr)
            else reject()
        }
        xhr.onerror = reject
        xhr.send(file)
    })
}

export const fetchMultipartUpload = async (
    context: { orgId: string; projectId: string; flightId: string; key: string },
    urls: string[],
    file: File,
    chunkSize = 4 * 1000 * 1000 * 1000,
    onProgress: (progress: number) => void,
    customRoute = "finish-multifile-upload"
) => {
    const parts = Math.ceil(file.size / chunkSize)
    const partInfo: { ETag: string; number: number }[] = []
    for (let i = 0; i < parts; i++) {
        const start = i * chunkSize
        const end = Math.min(start + chunkSize, file.size)
        const chunk = file.slice(start, end)
        const reader = new FileReader()
        reader.readAsArrayBuffer(chunk)
        const data = await new Promise<ArrayBuffer>((resolve, reject) => {
            reader.onload = () => resolve(reader.result as ArrayBuffer)
            reader.onerror = reject
        })
        const res = await uploadFileWithProgress(urls[i], data, (progress) => {
            onProgress((progress * (end - start) + start) / file.size)
        })
        const ETag = res.getResponseHeader("ETag")!
        partInfo.push({ ETag, number: i + 1 })
    }
    // finish multipart upload
    const uploadId = urls.at(-1)!
    return await fetch(
        `/api/organization/${context.orgId}/project/${context.projectId}/flight/${context.flightId}/${customRoute}?key=${context.key}&uploadId=${uploadId}`,
        { method: "POST" }
    )
}

export const fetchGlobalUpload: typeof fetch = (...args: Parameters<typeof fetch>) => {
    const promise = fetch(...args)

    return promise
}

export const useS3File = (s3Key: string | undefined, options: { disabled: boolean }) => {
    const key = options.disabled || !s3Key ? null : ["s3-file", s3Key]

    return useSWR(key, async () => {
        const res = await fileManagementApi.fetchFile(s3Key!)
        if (!res.ok) throw new Error("Fetch failed")
        const blob = await res.blob()
        return new File([blob], "file", { type: blob.type })
    })
}

export const useS3Metadata = (url: string | undefined, options: { disabled?: boolean } = {}) => {
    const key = options.disabled || !url ? null : ["metadata", url]

    return useSWR(key, async () => {
        const response = await fetch(url!, {
            method: "GET",
            headers: { Range: "bytes=0-0" },
        })

        if (!response.ok && response.status !== 206) {
            throw new Error(`Failed to fetch metadata: ${response.statusText}`)
        }

        return {
            type: response.headers.get("content-type")?.split("/").at(-1)?.toUpperCase(),
            lastModified: response.headers.get("last-modified"),
            etag: response.headers.get("etag")?.replace(/"/g, ""),
        }
    })
}

export const createSpeedCallback = (
    fn: (arg: {
        speedMbps: number
        bitsUploaded: number
        bytesUploaded: number
        percentUploaded: number
    }) => void
) => {
    const startTime = performance.now()

    return (loaded: number, total: number) => {
        const elapsedTimeSec = (performance.now() - startTime) / 1000 // in seconds
        const bytesUploaded = loaded
        const bitsUploaded = bytesUploaded * 8
        const megabitsUploaded = bitsUploaded / 1_000_000

        const speedMbps = megabitsUploaded / elapsedTimeSec
        fn({ speedMbps, bitsUploaded, bytesUploaded, percentUploaded: (loaded / total) * 100 })
    }
}
